import type * as React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type { JSX } from 'react'

import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type {
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import { Text } from '@buffer-mono/popcorn'

import type { SavedView } from '../hooks'

import styles from './DraggableSavedViewsProvider.module.css'

interface DraggableSavedViewsContextValue {
  isDragging: boolean
}

const DraggableSavedViewsContext = createContext<
  DraggableSavedViewsContextValue | undefined
>(undefined)

/** Read the dragging flag from `DraggableSavedViewsProvider`. */
export function useDraggableSavedViewsContext(): DraggableSavedViewsContextValue {
  const context = useContext(DraggableSavedViewsContext)
  if (!context) {
    throw new Error(
      'useDraggableSavedViewsContext must be used within DraggableSavedViewsProvider',
    )
  }
  return context
}

export interface DraggableSavedViewsProviderProps {
  views: SavedView[]
  onReorder: (newViewIds: string[]) => void
  children: React.ReactNode
}

/**
 * DnD wrapper for the saved-views list. Builds the new id order on drop
 * and forwards it to `onReorder`; the surrounding section calls the
 * existing `useReorderSavedViewsMutation` with that order.
 */
export function DraggableSavedViewsProvider({
  views,
  onReorder,
  children,
}: DraggableSavedViewsProviderProps): JSX.Element {
  const [draggedView, setDraggedView] = useState<SavedView | undefined>(
    undefined,
  )

  const isDragging = !!draggedView
  const contextValue = useMemo(() => ({ isDragging }), [isDragging])

  const ids = useMemo(() => views.map((view) => view.id), [views])

  const sensors = useSensors(
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
  )

  const handleDragStart = useCallback(
    (event: DragStartEvent): void => {
      const { active } = event
      if (!active.id) return
      setDraggedView(views.find((view) => view.id === active.id))
    },
    [views],
  )

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent): void => {
      setDraggedView(undefined)
      if (!over?.id || active.id === over.id) return

      const fromIndex = ids.indexOf(active.id.toString())
      const toIndex = ids.indexOf(over.id.toString())
      if (fromIndex === -1 || toIndex === -1) return

      onReorder(arrayMove(ids, fromIndex, toIndex))
    },
    [ids, onReorder],
  )

  const handleDragCancel = useCallback((): void => {
    setDraggedView(undefined)
  }, [])

  const announcements = useMemo(() => {
    const getPosition = (id: string): number => ids.indexOf(id.toString()) + 1
    const viewCount = ids.length
    const getViewName = (id: string): string =>
      views.find((view) => view.id === id.toString())?.name ?? 'Unknown view'

    return {
      onDragStart({ active }: DragStartEvent): string {
        return `Picked up saved view ${getViewName(
          active.id.toString(),
        )}. Saved view is in position ${getPosition(
          active.id.toString(),
        )} of ${viewCount}`
      },
      onDragOver({ active, over }: DragOverEvent): string {
        if (over) {
          return `Saved view ${getViewName(
            active.id.toString(),
          )} was moved into position ${getPosition(
            over.id.toString(),
          )} of ${viewCount}`
        }
        return `Saved view ${getViewName(
          active.id.toString(),
        )} is no longer over a droppable area`
      },
      onDragEnd({ active, over }: DragEndEvent): string {
        if (over) {
          return `Saved view ${getViewName(
            active.id.toString(),
          )} was dropped at position ${getPosition(
            over.id.toString(),
          )} of ${viewCount}`
        }
        return `Saved view ${getViewName(active.id.toString())} was dropped`
      },
      onDragCancel({ active }: DragCancelEvent): string {
        return `Dragging was cancelled. Saved view ${getViewName(
          active.id.toString(),
        )} was dropped.`
      },
    }
  }, [ids, views])

  return (
    <DraggableSavedViewsContext.Provider value={contextValue}>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        sensors={sensors}
        modifiers={[restrictToFirstScrollableAncestor]}
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable: `
              To pick up a saved view, press space or enter.
              Use the up and down arrow keys to change the view's position in the list.
              Press space or enter again to drop the view in its new position, or press escape to cancel.
            `,
          },
        }}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {children}
        </SortableContext>
        <DragOverlay>
          {draggedView && (
            <div className={styles.dragOverlayItem}>
              <Text>{draggedView.name}</Text>
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </DraggableSavedViewsContext.Provider>
  )
}
